import React, { useState } from 'react';
import Icon from "components/ui/AppIcon.jsx";
import Button from '../../../components/ui/Button.jsx';

const GlobalControls = () => {
  const [selectedCertification, setSelectedCertification] = useState('all');
  const [selectedPeriod, setSelectedPeriod] = useState('current');
  const [complianceThreshold, setComplianceThreshold] = useState(95);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const certificationTypes = [
    { value: 'all', label: 'Todas las Certificaciones' },
    { value: 'iso17025', label: 'ISO/IEC 17025' },
    { value: 'iso9001', label: 'ISO 9001' },
    { value: 'nist', label: 'NIST Traceability' },
    { value: 'oiml', label: 'OIML' },
    { value: 'ieee', label: 'IEEE Standards' }
  ];

  const auditPeriods = [
    { value: 'current', label: 'Período Actual' },
    { value: 'next_30', label: 'Próximos 30 días' },
    { value: 'next_90', label: 'Próximos 90 días' },
    { value: 'overdue', label: 'Vencidos' },
    { value: 'custom', label: 'Personalizado' }
  ];

  const handleRefresh = () => {
    setLastRefresh(new Date());
    // Simulate refresh action
    console.log('Refreshing compliance data...');
  };

  const handleThresholdChange = (e) => {
    setComplianceThreshold(parseInt(e?.target?.value));
  };

  const formatTime = (date) => {
    return date?.toLocaleTimeString('es-ES', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div className="mb-4 lg:mb-0">
          <h2 className="text-xl font-semibold text-foreground mb-1">
            Panel de Cumplimiento y Calidad
          </h2>
          <p className="text-sm text-muted-foreground">
            Monitoreo de certificaciones, auditorías y métricas de calidad
          </p>
        </div>

        <div className="flex items-center space-x-2 text-xs text-muted-foreground">
          <Icon name="Clock" size={14} />
          <span>Última actualización: {formatTime(lastRefresh)}</span>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        {/* Certification Type Filter */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">
            Tipo de Certificación
          </label>
          <select
            value={selectedCertification}
            onChange={(e) => setSelectedCertification(e?.target?.value)}
            className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {certificationTypes?.map(type => (
              <option key={type?.value} value={type?.value}>
                {type?.label}
              </option>
            ))}
          </select>
        </div>

        {/* Audit Period Selector */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">
            Período de Auditoría
          </label>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e?.target?.value)}
            className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {auditPeriods?.map(period => (
              <option key={period?.value} value={period?.value}>
                {period?.label}
              </option>
            ))}
          </select>
        </div>

        {/* Compliance Threshold */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">
            Umbral de Cumplimiento
          </label>
          <div className="relative">
            <input
              type="range"
              min="80"
              max="100"
              value={complianceThreshold}
              onChange={handleThresholdChange}
              className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer slider"
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>80%</span>
              <span className="font-medium text-primary">{complianceThreshold}%</span>
              <span>100%</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">
            Acciones Rápidas
          </label>
          <div className="flex flex-col space-y-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              iconName="RefreshCw"
              className="w-full"
            >
              Actualizar
            </Button>
            <Button
              variant="ghost"
              size="sm"
              iconName="Settings"
              className="w-full"
            >
              Configurar
            </Button>
          </div>
        </div>
      </div>
      {/* Quick Status Indicators */}
      <div className="flex flex-wrap items-center gap-4 mt-6 pt-4 border-t border-border">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-success rounded-full"></div>
          <span className="text-sm text-muted-foreground">Conforme</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-warning rounded-full"></div>
          <span className="text-sm text-muted-foreground">Advertencia</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-error rounded-full"></div>
          <span className="text-sm text-muted-foreground">No Conforme</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-primary rounded-full"></div>
          <span className="text-sm text-muted-foreground">Pendiente</span>
        </div>
        
        <div className="ml-auto flex items-center space-x-4">
          <Button variant="outline" size="sm" iconName="Download">
            Exportar Reporte
          </Button>
          <Button variant="default" size="sm" iconName="Plus">
            Nueva Auditoría
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GlobalControls;
